import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { formatUK } from '../../lib/ukTime';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Clock,
  History,
  LogIn,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupervisorMyHistoryPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/clock-events/my`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch clock history');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    return formatUK(isoString, 'dd MMM yyyy, HH:mm');
  };

  const formatDate = (isoString) => {
    return formatUK(isoString, 'EEE, dd MMM yyyy');
  };

  const formatTime = (isoString) => {
    return formatUK(isoString, 'HH:mm');
  };

  // Group events by date
  const groupedEvents = events.reduce((groups, event) => {
    const date = formatUK(event.timestamp, 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedEvents).sort((a, b) => b.localeCompare(a));

  return (
    <SupervisorLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b border-border pb-6">
          <h1 className="text-2xl font-barlow tracking-wider text-primary uppercase flex items-center gap-2">
            <History className="w-6 h-6" />
            My Clock History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View your clock in/out records
          </p>
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-8">Loading...</p>
        ) : events.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No clock history found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sortedDates.map(date => (
              <Card key={date} className="bg-card border-border">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase text-muted-foreground">
                    {formatDate(date + 'T00:00:00')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {groupedEvents[date].map(event => (
                    <div
                      key={event.id}
                      className={`flex items-center justify-between p-3 rounded-sm border ${
                        event.event_type === 'clock_in' 
                          ? 'bg-green-900/10 border-green-900/30' 
                          : 'bg-red-900/10 border-red-900/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {event.event_type === 'clock_in' ? (
                          <LogIn className="w-5 h-5 text-green-500" />
                        ) : (
                          <LogOut className="w-5 h-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">
                            {event.event_type === 'clock_in' ? 'Clock In' : 'Clock Out'}
                          </p>
                          {event.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{event.notes}</p>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className={
                        event.event_type === 'clock_in' 
                          ? 'border-green-700 text-green-400' 
                          : 'border-red-700 text-red-400'
                      }>
                        {formatTime(event.timestamp)}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </SupervisorLayout>
  );
}
